import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Outlet, useParams } from "react-router-dom";
import { Frame, TopBar, Loading, Icon, Text } from "@shopify/polaris";
import { ExitIcon, ProfileIcon, PlusCircleIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import {
  checkSession,
  logout,
  toogleUserMenu,
  toogleNav,
} from "features/auth/authSlice";
import Modalx from "components/modalx/modalx";
import { setModalxComponent } from "components/modalx/modalx.slice";
import {
  fetchCompanies,
  fetchCompanyGroups,
} from "features/company-select/company-select.slice";
import "./styles.scss";
import PendingOnboardingBanner from "components/pending-onboarding-banner/pending-onboarding-banner";

import { APP_CONFIG } from "../../config/app-config";

function AppLayout() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { session, role, isLoading, userMenuActive, isError } = useSelector(
    (state) => state.authReducer
  );

  useEffect(() => {
    if (isError) {
      window.asist = { redirect: window.location.pathname };
      navigate("/auth/login");
    } else if (!session && !isLoading) {
      dispatch(checkSession());
    }
  }, [navigate, dispatch, session, isLoading, isError]);

  // Redirect non-activated users to onboarding-only layout
  useEffect(() => {
    if (session && session.hasPendingOnboarding) {
      navigate("/app/my-onboarding", { replace: true });
    }
  }, [session, navigate]);

  // Only once the session is known. This used to fire on mount alongside
  // checkSession, so the first load after the activation redirect could ask
  // for companies before being authenticated, get nothing, and leave the user
  // on an empty page that a manual reload then fixed.
  useEffect(() => {
    if (session) {
      dispatch(fetchCompanies());
      dispatch(fetchCompanyGroups());
    }
  }, [dispatch, session]);

  const { companiesCollectionRaw } = useSelector(
    (state) => state.companySelectReducer
  );

  if (!session) {
    return (
      <Frame>
        <Loading />
      </Frame>
    );
  }

  const handleLogOut = async () => {
    await dispatch(logout());
  };

  const handleToogleUserMenu = async () => {
    await dispatch(toogleUserMenu());
  };

  const handleNavigationToggle = async () => {
    await dispatch(toogleNav());
  };

  const handleCompanyChange = (id) => {
    if (companyId) {
      navigate(`/app/${id}`);
    }
  };

  // A guest member has only «About me» in the cabinet.
  const handleOpenCabinet = () => {
    if (companiesCollectionRaw.length > 0) {
      const companyId = companiesCollectionRaw[0].id;
      navigate(
        role?.isUserGuest
          ? `/app/${companyId}/profile/${session.id}/info`
          : `/app/${companyId}/my-work`,
        { replace: true }
      );
    }
  };

  const handleCreateCompany = () => {
    dispatch(setModalxComponent("CompanyForm"));
  };

  const userMenuActions = [
    {
      items: [
        {
          content: t("cabinet_group"),
          icon: ProfileIcon,
          onAction: handleOpenCabinet,
        },
        { content: t("log_out"), icon: ExitIcon, onAction: handleLogOut },
      ],
    },
  ];

  const userMenuMarkup = (
    <TopBar.UserMenu
      actions={userMenuActions}
      name={session.email}
      open={userMenuActive}
      onToggle={handleToogleUserMenu}
    />
  );

  const Logo = (
    <div
      className="logo_text"
      style={{
        color: "#fff",
        height: "100%",
        width: "100%",
        cursor: "pointer",
      }}
      onClick={() => navigate("/app")}
    >
      <Text as="h1" fontWeight="semibold">
        {APP_CONFIG.isUhbGroup ? "UHB Group" : APP_CONFIG.siteName}
      </Text>
    </div>
  );

  const topBarMarkup = (
    <div className="company_select_box">
      <TopBar
        // secondaryMenu={<CompanySelect onChange={handleCompanyChange} />}
        secondaryMenu={
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <button
              type="button"
              className="notification-parent"
              onClick={handleCreateCompany}
              aria-label={t("create_company")}
            >
              <Icon source={PlusCircleIcon} tone="base" />
            </button>
          </div>
        }
        userMenu={userMenuMarkup}
        onNavigationToggle={handleNavigationToggle}
        logoSuffix={Logo}
      />
    </div>
  );

  if (companyId) {
    return <Outlet />;
  } else {
    return (
      <div className="main_content">
        <Frame
          topBar={topBarMarkup}
          onNavigationDismiss={handleNavigationToggle}
          logo={Logo}
        >
          <PendingOnboardingBanner />
          <Outlet />
          <Modalx />
        </Frame>
      </div>
    );
  }
}

export default AppLayout;
